import { cn } from "@/lib/utils"
import { tokenProps$ } from "@/state/chain"
import { amountToParts } from "@/util/format"
import type { TokenProperties } from "@polkadot-api/react-components"
import { useStateObservable } from "@react-rxjs/core"
import type { FC } from "react"

const decimalPoint = (0.1).toLocaleString().slice(1, 2)

export const significantDigitsDecimals =
  (significantDigits: number, min: number = 0) =>
  (value: bigint, tokenDecimals: number) => {
    const TOKEN_UNIT = 10n ** BigInt(tokenDecimals)
    const integerLength =
      value / TOKEN_UNIT === 0n ? 0 : (value / TOKEN_UNIT).toString().length
    return Math.max(min, significantDigits - integerLength)
  }

export const fixedDecimals = (decimals: number) => () => decimals
export const allDecimals = () => (_: bigint, tokenDecimals: number) =>
  tokenDecimals

export function formatToken(
  value: bigint,
  tokenProps: TokenProperties,
  decimalsFn: (
    integerPart: bigint,
    tokenDecimals: number,
  ) => number = significantDigitsDecimals(3, 2),
) {
  const { decimals: tokenDecimals, symbol } = tokenProps

  const { integer, fraction } = amountToParts(value, tokenDecimals)

  const decimals = decimalsFn(value, tokenDecimals)
  const decimalPart =
    decimals > 0
      ? `${decimalPoint}${fraction
          .slice(0, decimals)
          .replace(/0+$/, "")
          .padEnd(decimals, "0")}`
      : null
  return Number(integer).toLocaleString() + (decimalPart || "") + " " + symbol
}

export const TokenValue: FC<{
  value: bigint | null | undefined
  decimalsFn?: (integerPart: bigint, tokenDecimals: number) => number
  className?: string
  colored?: boolean
}> = ({
  value,
  decimalsFn = significantDigitsDecimals(2, 2),
  className,
  colored = true,
}) => {
  const tokenProps = useStateObservable(tokenProps$)
  if (value == null) return `-`
  if (!tokenProps) return `…`
  const { decimals: tokenDecimals, symbol } = tokenProps

  const { integer, fraction } = amountToParts(value, tokenDecimals)

  const decimals = decimalsFn(value, tokenDecimals)
  const fractionPart =
    decimals > 0
      ? fraction.slice(0, decimals).replace(/0+$/, "").padEnd(decimals, "0")
      : ""
  const integerPart = Number(integer).toLocaleString()
  const decimalPart =
    Number(fractionPart) > 0 ? `${decimalPoint}${fractionPart}` : null
  const fullFractionPart = fraction.replace(/0*$/, "")
  const hasHiddenDecimals = fullFractionPart.length > fractionPart.length

  return (
    <span
      className={cn(colored ? "text-foreground" : "", className)}
      title={
        hasHiddenDecimals
          ? `${integerPart}${decimalPoint}${fullFractionPart} ${symbol}`
          : undefined
      }
    >
      <span>{integerPart}</span>
      {decimalPart && (
        <span className={colored ? "text-foreground/75" : undefined}>
          {decimalPart}
        </span>
      )}
      <span> {symbol}</span>
    </span>
  )
}
